import type { Butler } from "@evidenceb/ai-butler";
import type { ThemeType } from "@evidenceb/athena-common/modules/Theme";
import type { AthenaEvent } from "@evidenceb/athena-event-storage-schemas";
import type { PerModule } from "@evidenceb/gameplay-interfaces";
import type { Statement } from "@xapi/xapi";
import type { AIError } from "../hooks/useAI/useAI.types";
import type { DuoParticipant } from "../pages/Workmodes/Duo/Duo.types";
import type {
  ExperimentationSet,
  Test,
} from "../pages/Workmodes/Experimentation/type";
import type { Maybe } from "../utils/types";
import type { PRLockStatus } from "./PedagogicalResourcesManagement";
import type { ErrorLoader, Loader } from "./Status";
import type { UserExtra, UserType } from "./User";

export interface Session {
  // COMMON

  sessionId: string;
  userId: string;
  firstname: string;
  lastname: string;
  userProvider: string;
  appProvider: string;
  appVariation: string;
  userType: UserType;
  evidencebId: string;
  version: string;
  extra: Partial<UserExtra>;
  theme: ThemeType;
  flags: {
    useHistoryFrom: StatementHistory;
    displaySignIn: boolean;
    /**
     * In the player, allow to see the exercise hierarchy & result in the console (for POs & AI team)
     * @default false
     */
    logExerciseDetails: boolean;
  };
  dismissMessages: { [key in DismissableMessages]: boolean };
  /** Effective fragments: system, shared and individual lists merged in order. */
  fragments: string[];

  specimen?: boolean;
  /**
   * App shortname
   * NB: only available for authentication
   */
  app?: string;

  // STUDENT
  soloAI: Maybe<PerModule<ErrorLoader<Butler, AIError>>>;
  /**
   * Whether the statements were fetched from the LRS
   */
  statementsFetched: boolean;
  statements?: Statement[];
  /** All locally-stored AthenaEvents. */
  athenaEvents: AthenaEvent<any, any>[];
  prLockStatus?: Loader<PRLockStatus>;
  duos?: DuoSession[];
  resourceAssignments?: // Word of caution: For now assignments are only fetched at the
  // initialization of the app or when the user has a new assignment
  // notification. When refactoring with react-query, make sure that when
  // new assignments are received, notifications are also refetched to get
  // the notification associated with the new assignement. See
  // src/components/Notifications/api/useQueries.ts for the reverse sync
  // that is already in place (new notif triggers refetching assignments)
  ResourceAssignment[];
  classroomIds?: string[];

  // TEACHER
  school?: string;
  classrooms?: Group[];
  groups?: Group[];

  // EXPERIMENTATION tests
  experimentationTests?: ExperimentationSet<Test>[];
}
export default Session;

export type DuoSession = {
  id: string;
  name: string;
  moduleId: string;
  mentee: DuoParticipant;
  mentor: DuoParticipant;
  status: DuoStatus;
  teacherId: string;
};

export enum DuoStatus {
  Inactive = "inactive",
  Active = "active",
  Completed = "completed",
}

export enum DismissableMessages {
  prmObjectiveLockingConfirmationModal = "prmObjectiveLockingConfirmationModal",
  prmObjectiveUnlockingConfirmationModal = "prmObjectiveUnlockingConfirmationModal",
  specimenGetFullVersionBanner = "specimenGetFullVersionBanner",
  specimenSwitchUserModal = "specimenSwitchUserModal",
  specimenTeacherDashboardModal = "specimenTeacherDashboardModal",
  playlistRemoveStudentFromRecipients = "playlistRemoveStudentFromRecipients",
  offlineBanner = "offlineBanner",
  deppTestManual = "deppTestManual",
  dashboardHelpInfo = "dahsboardHelpInfo",
}

export enum StatementHistory {
  localHistory = "LOCAL_HISTORY",
  noHistory = "NO_HISTORY",
  LRS = "LRS",
}

export type Group = {
  id: string;
  name: string;
  lockStatus: PRLockStatus;
  students: Student[];
  external_id: string;
};

export function isGroup(obj: any): obj is Group {
  return (
    typeof obj === "object" &&
    obj !== null &&
    obj.lockStatus !== null &&
    obj.duos !== null
  );
}

export type Student = {
  id: string;
  firstname: string;
  lastname: string;
  extra?: Partial<UserExtra>;
};

export type ResourceAssignment = {
  teacher: string;
  assigned_resource: {
    resource_id: string;
    resource_type: "playlist" | "workshop";
    extra: any;
  };
  extra: any;
};
